import PropTypes from "prop-types";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/shared/context/AuthContext";
import AdminRoute from "@/components/features/auth/AdminRoute";
import ProtectedRoute from "@/components/features/auth/ProtectedRoute";
import PublicRoute from "@/components/features/auth/PublicRoute";
import CataButton from "@/components/common/CataButton";
import FreelancerOnboardingPage from "@/pages/freelancer/FreelancerOnboarding.jsx";
import {
  ACCOUNT_ONBOARDING_PATH,
  FREELANCER_DASHBOARD,
  canAccessDashboard,
} from "@/shared/lib/dashboard-preference";

const Home = lazy(() => import("@/pages/Home.jsx"));
const Marketplace = lazy(() => import("@/components/pages/Marketplace"));
const About = lazy(() => import("@/components/sections/home/About.jsx"));
const Contact = lazy(() => import("@/components/sections/home/Contact.jsx"));
const ClientDashboard = lazy(
  () => import("@/pages/client/ClientDashboard.jsx"),
);
const ClientProposal = lazy(
  () => import("@/components/features/client/ClientProposal.jsx"),
);
const ProposalDrafts = lazy(
  () => import("@/components/features/client/ProposalDrafts.jsx"),
);
const ClientProjects = lazy(
  () => import("@/pages/client/ClientProjects.jsx"),
);
const ClientProjectDetail = lazy(
  () => import("@/components/pages/ClientProjectDetailPage.jsx"),
);
const ClientStagingPage = lazy(
  () => import("@/pages/client/ClientStagingPage.jsx"),
);
const ProjectAuditPage = lazy(
  () => import("@/pages/client/ProjectAuditPage.jsx"),
);
const ClientChat = lazy(
  () => import("@/components/features/client/ClientChat.jsx"),
);
const AIChat = lazy(() => import("@/components/features/ai/AIChat.jsx"));
const ClientProfile = lazy(
  () => import("@/components/features/client/ClientProfile.jsx"),
);
const ClientPayments = lazy(
  () => import("@/components/features/client/ClientPayments.jsx"),
);
const ProjectManagerDashboard = lazy(
  () => import("@/modules/project-manager/pages/DashboardPage"),
);
const ManagerAvailability = lazy(
  () => import("@/modules/project-manager/pages/AvailabilityPage"),
);
const ManagerAppointments = lazy(
  () => import("@/modules/project-manager/pages/CalendarPage"),
);
const ManagerProjects = lazy(
  () => import("@/modules/project-manager/pages/ProjectsPage"),
);
const ManagerProjectDetail = lazy(
  () => import("@/modules/project-manager/pages/ProjectDetailsPage"),
);
const ManagerChat = lazy(
  () => import("@/modules/project-manager/pages/MessagesPage"),
);
const ManagerProfile = lazy(
  () => import("@/modules/project-manager/pages/ProfessionalProfilePage"),
);
const ManagerMarketplace = lazy(
  () => import("@/modules/project-manager/pages/MarketplacePage"),
);
const ManagerReports = lazy(
  () => import("@/modules/project-manager/pages/ReportsPage"),
);
const ManagerProjectSetup = lazy(
  () => import("@/modules/project-manager/pages/ProjectSetupPage"),
);
const EmailAuthPage = lazy(() => import("@/components/Forms/EmailAuth"));
const PhoneAuthPage = lazy(() => import("@/components/Forms/PhoneAuth"));
const PhoneRoleOnboarding = lazy(
  () => import("@/components/features/auth/PhoneRoleOnboarding"),
);
const ForgotPasswordPage = lazy(
  () => import("@/components/features/auth/forms/ForgotPassword"),
);
const ResetPasswordPage = lazy(
  () => import("@/components/features/auth/forms/ResetPassword"),
);
const PMLogin = lazy(
  () => import("@/components/features/project-manager/PMLogin"),
);
const PMRegister = lazy(
  () => import("@/components/features/project-manager/PMRegister"),
);
const FreelancerDashboard = lazy(
  () => import("@/pages/freelancer/FreelancerDashboard.jsx"),
);
const FreelancerGrowthQuest = lazy(
  () => import("@/components/features/engagement/FreelancerGrowthQuestPage.jsx"),
);
const FreelancerGrowthQuestContestDetail = lazy(
  () => import("@/components/features/engagement/FreelancerGrowthQuestContestDetailPage.jsx"),
);
const FreelancerProposal = lazy(
  () => import("@/components/features/freelancer/FreelancerProposal"),
);
const FreelancerProfile = lazy(
  () => import("@/pages/freelancer/FreelancerProfile.jsx"),
);
const FreelancerProjects = lazy(
  () => import("@/components/features/freelancer/FreelancerProjects"),
);
const FreelancerProjectDetail = lazy(
  () => import("@/components/pages/FreelancerProjectDetailPage.jsx"),
);
const FreelancerChat = lazy(
  () => import("@/components/features/freelancer/FreelancerChat"),
);
const FreelancerPayments = lazy(
  () => import("@/components/features/freelancer/FreelancerPayments"),
);
const AgencyOnboardingPage = lazy(
  () => import("@/pages/agency/AgencyOnboarding.jsx"),
);
const NotepadPage = lazy(() => import("@/components/pages/notepad-page"));
const AdminDashboard = lazy(
  () => import("@/components/features/admin/AdminDashboard"),
);
const AdminPayoutRequests = lazy(
  () => import("@/components/features/admin/AdminPayoutRequests"),
);
const AdminAiUsage = lazy(
  () => import("@/components/features/admin/AdminAiUsage"),
);
const AdminUsers = lazy(() => import("@/components/features/admin/AdminUsers"));
const AdminProjects = lazy(
  () => import("@/components/features/admin/AdminProjects"),
);
const AdminProjectDetail = lazy(
  () => import("@/components/features/admin/AdminProjectDetail"),
);
const AdminDisputes = lazy(
  () => import("@/components/features/admin/AdminDisputes"),
);
const AdminLogin = lazy(() => import("@/components/features/admin/AdminLogin"));
const AdminApprovals = lazy(
  () => import("@/components/features/admin/AdminApprovals"),
);
const AdminUserRequests = lazy(
  () => import("@/components/features/admin/AdminUserRequests"),
);
const AdminWhatsappInbox = lazy(
  () => import("@/components/features/admin/AdminWhatsappInbox"),
);
const AdminWhatsappAnalytics = lazy(
  () => import("@/components/features/admin/AdminWhatsappAnalytics"),
);

const AdminFreelancerOnboarding = lazy(

  () => import("@/components/features/admin/AdminFreelancerOnboarding"),
);
const AdminServicesActivity = lazy(
  () => import("@/components/features/admin/AdminServicesActivity"),
);
const AdminServicesActivityDetail = lazy(
  () => import("@/components/features/admin/AdminServicesActivityDetail"),
);
const AdminFreelancerLimits = lazy(
  () => import("@/components/features/admin/AdminFreelancerLimits"),
);
const AdminUserDetails = lazy(
  () => import("@/components/features/admin/AdminUserDetails"),
);
const AdminServices = lazy(
  () => import("@/components/features/admin/AdminServices"),
);
const AdminCategoriesSkills = lazy(
  () => import("@/components/features/admin/AdminCategoriesSkills"),
);
const AdminServiceQuestions = lazy(
  () => import("@/components/features/admin/AdminServiceQuestions"),
);
const AdminFreelancerOnboardingContent = lazy(
  () => import("@/components/features/admin/AdminFreelancerOnboardingContent"),
);
const AdminBlogs = lazy(
  () => import("@/components/features/admin/AdminBlogs"),
);
const AdminContactInquiries = lazy(
  () => import("@/components/features/admin/AdminContactInquiries"),
);
const AdminEngagementOverview = lazy(
  () => import("@/components/features/engagement/AdminEngagementOverview.jsx"),
);
const AdminEngagementQuestions = lazy(
  () => import("@/components/features/engagement/AdminEngagementQuestions.jsx"),
);
const GetStarted = lazy(() => import("@/components/features/auth/GetStarted"));
const EnterpriseSolutions = lazy(
  () => import("@/components/pages/EnterpriseSolutions"),
);
const Blog = lazy(() => import("@/components/pages/Blog"));
const BlogPost = lazy(() => import("@/components/pages/BlogPost"));
const BlogUpload = lazy(() => import("@/components/pages/BlogUpload"));
const BlogManage = lazy(() => import("@/components/pages/BlogManage"));
const HelpCenter = lazy(() => import("@/components/pages/HelpCenter"));
const TermsOfService = lazy(() => import("@/components/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/components/pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("@/components/pages/RefundPolicy"));
const FeesPricing = lazy(() => import("@/components/pages/FeesPricing"));
const Security = lazy(() => import("@/components/pages/Security"));
import Loader from "@/components/common/Loader";

const GuestAIDemo = lazy(() => import("@/components/pages/GuestAIDemo"));
const ServiceDetails = lazy(() => import("@/components/pages/ServiceDetails"));

const RouteFallback = () => <Loader />;

const LegacyLoginRedirect = () => {
  const location = useLocation();

  return (
    <Navigate
      replace
      state={location.state}
      to={{
        pathname: "/signin/phone",
        search: location.search,
      }}
    />
  );
};

const isWorkspacePath = (pathname = "") => {
  const p = String(pathname || "").trim().toLowerCase();
  if (p.includes("/onboarding")) {
    return false;
  }
  return p.startsWith("/client") || p.startsWith("/freelancer");
};

const DashboardRouteTransitionLoader = () => {
  const { pathname } = useLocation();
  const previousPathRef = useRef(pathname);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = pathname;

    if (previousPath === pathname) {
      return undefined;
    }

    if (!isWorkspacePath(previousPath) && !isWorkspacePath(pathname)) {
      setIsVisible(false);
      return undefined;
    }

    setIsVisible(true);
    const timeoutId = window.setTimeout(() => setIsVisible(false), 300);

    return () => window.clearTimeout(timeoutId);
  }, [pathname]);

  if (!isVisible) {
    return null;
  }

  return (
    <Loader className="fixed inset-0 z-[120] bg-background" />
  );
};

const NoIndexRoute = ({ children }) => {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  return children;
};

const App = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (isWorkspacePath(pathname)) {
      document.documentElement.classList.add("workspace-zoom");
      document.documentElement.style.zoom = "0.75";
    } else {
      document.documentElement.classList.remove("workspace-zoom");
      document.documentElement.style.zoom = "";
    }
  }, [pathname]);

  return (
    <main>
      <Suspense fallback={<RouteFallback />}>
          <DashboardRouteTransitionLoader />
          <Routes>
            <Route path="/ai-demo" element={<NoIndexRoute><GuestAIDemo /></NoIndexRoute>} />
            <Route
              path="/"
              element={
                <LayoutWithNavbar>
                  <Home />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/marketplace/:categorySlug?"
              element={
                <LayoutWithNavbar>
                  <Marketplace />
                </LayoutWithNavbar>
              }
            />
            <Route path="/opportunity" element={<OpportunityRoute />} />
            <Route
              path="/marketplace/service/:id"
              element={
                <LayoutWithNavbar>
                  <ServiceDetails />
                </LayoutWithNavbar>
              }
            />
            <Route path="/get-started" element={<NoIndexRoute><GetStarted /></NoIndexRoute>} />
            <Route path="/signup" element={<NoIndexRoute><PublicRoute><LegacyLoginRedirect /></PublicRoute></NoIndexRoute>} />
            <Route path="/signin/phone" element={<NoIndexRoute><PublicRoute><PhoneAuthPage /></PublicRoute></NoIndexRoute>} />
            <Route path="/signin/email" element={<NoIndexRoute><PublicRoute><EmailAuthPage /></PublicRoute></NoIndexRoute>} />
            <Route
              path={ACCOUNT_ONBOARDING_PATH}
              element={
                <ProtectedRoute loginPath="/signin/phone">
                  <PhoneRoleOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/about"
              element={
                <LayoutWithNavbar>
                  <About />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/talent"
              element={<Navigate replace to="/marketplace?view=freelancers#specialists-section" />}
            />
            <Route
              path="/enterprise"
              element={
                <LayoutWithNavbar>
                  <EnterpriseSolutions />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog/manage"
              element={
                <LayoutWithNavbar>
                  <BlogManage />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog/articles"
              element={
                <LayoutWithNavbar>
                  <BlogManage />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog/write"
              element={
                <LayoutWithNavbar>
                  <BlogUpload />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog/upload"
              element={
                <LayoutWithNavbar>
                  <BlogUpload />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog/new"
              element={
                <LayoutWithNavbar>
                  <BlogUpload />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog/:slug"
              element={
                <LayoutWithNavbar>
                  <BlogPost />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/blog"
              element={
                <LayoutWithNavbar>
                  <Blog />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/help"
              element={
                <LayoutWithNavbar>
                  <HelpCenter />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/terms"
              element={
                <LayoutWithNavbar>
                  <TermsOfService />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/privacy"
              element={
                <LayoutWithNavbar>
                  <PrivacyPolicy />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/refund-policy"
              element={
                <LayoutWithNavbar>
                  <RefundPolicy />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/fees-pricing"
              element={
                <LayoutWithNavbar>
                  <FeesPricing />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/security"
              element={
                <LayoutWithNavbar>
                  <Security />
                </LayoutWithNavbar>
              }
            />
            <Route
              path="/services"
              element={
                <LayoutServices>
                  <GuestAIDemo />
                </LayoutServices>
              }
            />
            <Route
              path="/contact"
              element={
                <LayoutWithNavbar>
                  <Contact />
                </LayoutWithNavbar>
              }
            />
            <Route path="/login" element={<NoIndexRoute><PublicRoute><LegacyLoginRedirect /></PublicRoute></NoIndexRoute>} />
            <Route path="/forgot-password" element={<NoIndexRoute><PublicRoute><LayoutWithNavbar><ForgotPasswordPage /></LayoutWithNavbar></PublicRoute></NoIndexRoute>} />
            <Route path="/reset-password" element={<NoIndexRoute><PublicRoute><LayoutWithNavbar><ResetPasswordPage /></LayoutWithNavbar></PublicRoute></NoIndexRoute>} />
            <Route path="/project-manager/login" element={<NoIndexRoute><PublicRoute><PMLogin /></PublicRoute></NoIndexRoute>} />
            <Route path="/project-manager/register" element={<NoIndexRoute><PublicRoute><PMRegister /></PublicRoute></NoIndexRoute>} />
            <Route
              path="/client"
              element={
                <ProtectedRoute>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/ai-chat"
              element={
                <ProtectedRoute>
                  <AIChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/project"
              element={
                <ProtectedRoute>
                  <ClientProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/project/:projectId"
              element={
                <ProtectedRoute>
                  <ClientProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/project/:projectId/staging"
              element={
                <ProtectedRoute>
                  <ClientStagingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/project/:projectId/audit"
              element={
                <ProtectedRoute>
                  <ProjectAuditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/proposal"
              element={
                <ProtectedRoute>
                  <ClientProposal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/proposal/drafts"
              element={
                <ProtectedRoute>
                  <ProposalDrafts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/messages"
              element={
                <ProtectedRoute>
                  <ClientChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/profile"
              element={
                <ProtectedRoute>
                  <ClientProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/payments"
              element={
                <ProtectedRoute>
                  <ClientPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ProjectManagerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/availability"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerAvailability />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/appointments"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerAppointments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/projects"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/projects/:projectId"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/messages"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/profile/:id"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/profile"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/marketplace"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerMarketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/reports"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project-manager/create-project"
              element={
                <ProtectedRoute
                  loginPath="/project-manager/login"
                  allowedRoles={["PROJECT_MANAGER", "ADMIN"]}
                >
                  <ManagerProjectSetup />
                </ProtectedRoute>
              }
            />
            <Route path="/service" element={<Navigate replace to="/services" />} />
            <Route
              path="/freelancer"
              element={
                <ProtectedRoute>
                  <FreelancerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/growth-quest"
              element={
                <ProtectedRoute>
                  <LayoutNavbarOnly>
                    <FreelancerGrowthQuest />
                  </LayoutNavbarOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/growth-quest/contests/:contestId"
              element={
                <ProtectedRoute>
                  <LayoutNavbarOnly>
                    <FreelancerGrowthQuestContestDetail />
                  </LayoutNavbarOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/proposals"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerProposal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/proposals/received"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerProposal filter="received" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/proposals/accepted"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerProposal filter="accepted" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/project"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/project/:projectId"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/messages"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/payments"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/onboarding/:stepId?"
              element={
                <ProtectedRoute
                  allowFreelancerOnboardingOnly
                  loginPath="/signin/phone?role=freelancer&redirect=/freelancer/onboarding"
                >
                  <FreelancerOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agency/onboarding/:stepId?"
              element={
                <ProtectedRoute
                  allowFreelancerOnboardingOnly
                  loginPath="/signin/phone?role=freelancer&redirect=/agency/onboarding"
                >
                  <AgencyOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freelancer/profile"
              element={
                <ProtectedRoute requireFreelancerOnboardingComplete>
                  <FreelancerProfile />
                </ProtectedRoute>
              }
            />
            <Route path="/notepad" element={<NoIndexRoute><NotepadPage /></NoIndexRoute>} />
            <Route path="/admin/login" element={<NoIndexRoute><PublicRoute><AdminLogin /></PublicRoute></NoIndexRoute>} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/payout-requests"
              element={
                <AdminRoute>
                  <AdminPayoutRequests />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminUsers roleFilter="ALL" />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <AdminRoute>
                  <AdminUsers roleFilter="CLIENT" />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/ai-usage"
              element={
                <AdminRoute>
                  <AdminAiUsage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/freelancers"
              element={
                <AdminRoute>
                  <AdminUsers roleFilter="FREELANCER" />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/freelancer-limits"
              element={
                <AdminRoute>
                  <AdminFreelancerLimits />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/freelancer-onboarding"
              element={
                <AdminRoute>
                  <AdminFreelancerOnboarding />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/services-activity"
              element={
                <AdminRoute>
                  <AdminServicesActivity />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/services-activity/:sessionId"
              element={
                <AdminRoute>
                  <AdminServicesActivityDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/whatsapp-inbox"
              element={
                <AdminRoute>
                  <AdminWhatsappInbox />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/whatsapp-analytics"
              element={
                <AdminRoute>
                  <AdminWhatsappAnalytics />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/project-managers"
              element={
                <AdminRoute>
                  <AdminDisputes />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/services"
              element={
                <AdminRoute>
                  <AdminServices />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/categories-skills"
              element={
                <AdminRoute>
                  <AdminCategoriesSkills />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/service-questions"
              element={
                <AdminRoute>
                  <AdminServiceQuestions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/onboarding-content"
              element={
                <AdminRoute>
                  <AdminFreelancerOnboardingContent />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/engagement"
              element={
                <AdminRoute>
                  <AdminEngagementOverview />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/engagement/questions"
              element={
                <AdminRoute>
                  <AdminEngagementQuestions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/blogs"
              element={
                <AdminRoute>
                  <AdminBlogs />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contact-inquiries"
              element={
                <AdminRoute>
                  <AdminContactInquiries />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <AdminRoute>
                  <AdminProjects />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                <AdminRoute>
                  <AdminUserDetails />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/projects/:id"
              element={
                <AdminRoute>
                  <AdminProjectDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/approvals"
              element={
                <AdminRoute>
                  <AdminApprovals />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/user-requests"
              element={
                <AdminRoute>
                  <AdminUserRequests />
                </AdminRoute>
              }
            />
            <Route
              path="*"
              element={
                <LayoutWithNavbar>
                  <NotFound />
                </LayoutWithNavbar>
              }
            />
          </Routes>
        </Suspense>
        <CataButton />
    </main>
  );
};

const OpportunityRoute = () => {
  const { isAuthenticated, isCheckingAuth, user } = useAuth();
  const canAccessFreelancerDashboard =
    isAuthenticated && canAccessDashboard(user, FREELANCER_DASHBOARD);

  if (isCheckingAuth) {
    return <RouteFallback />;
  }

  if (!canAccessFreelancerDashboard) {
    return <Navigate to="/marketplace" replace />;
  }

  return (
    <LayoutWithNavbar>
      <Marketplace />
    </LayoutWithNavbar>
  );
};

const LayoutWithNavbar = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

LayoutWithNavbar.propTypes = {
  children: PropTypes.node.isRequired,
};

const LayoutNavbarOnly = ({ children }) => (
  <>
    <Navbar />
    {children}
  </>
);

LayoutNavbarOnly.propTypes = {
  children: PropTypes.node.isRequired,
};

const LayoutServices = ({ children }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasChatParam = Boolean(searchParams.get("chat"));

  return (
    <>
      <Navbar />
      {children}
      {!hasChatParam && <Footer />}
    </>
  );
};

LayoutServices.propTypes = {
  children: PropTypes.node.isRequired,
};

const NotFound = () => (
  <main className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6">
    <p className="text-sm uppercase tracking-[0.3em] text-emerald-400/80">
      404
    </p>
    <h1 className="text-3xl md:text-4xl font-light">Page not found</h1>
    <p className="text-emerald-50/70 max-w-md">
      The route you are looking for doesn&apos;t exist. Use the main navigation
      to head back home.
    </p>
  </main>
);

export default App;

